import { supabase } from '@/integrations/supabase/client';
import type { ClienteSubdivisaoRegra } from '@/types/clienteSubdivisao';
import { aplicarRegrasDesmembramento } from '@/utils/ordensServico/clienteSubdivisao';
import { limparCacheRegras, resolveValorHoraAposRegra } from '@/utils/analises/aplicarRegrasDesmembramento';
import { convertHoursToDecimal } from '@/utils/ordensServico/ordensServicoTimeUtils';
import { executeReprocessamentoCustosJob } from '@/services/taskAgenda/executeReprocessamentoCustosJob';
import { startOfMonthYmdSaoPaulo, todayYmdSaoPaulo } from '@/services/taskAgenda/taskAgendaSchedule';

export type ExecuteRegrasDesmembramentoParams = {
  /** Filtro opcional em `data_real` (YYYY-MM-DD). Vazio = todas as OS. */
  dataInicio?: string | null;
  dataFim?: string | null;
  batchSize?: number;
  /** Alíquota decimal (ex.: 0,13). Usada para recalcular imposto após mudar cliente/faturamento. */
  impostoPercentual?: number;
  /** Interrompe entre lotes (ex.: botão pausar na tela). */
  signal?: AbortSignal;
  /**
   * Se true, roda o reprocessamento de custos no período (altera `valor_a_pagar` / MOB).
   * Padrão: false — o job já ajusta faturamento/imposto e o fluxo manual preserva MOB.
   */
  recalcularCustosDepois?: boolean;
  onProgress?: (s: {
    processed: number;
    total: number;
    regrasAplicadas: number;
    errors: number;
  }) => void;
};

type OrdemRow = {
  /** PK no Supabase (geralmente UUID string). */
  id: string | number;
  numero: string;
  empresa_nome: string | null;
  empresa_id: number | null;
  operador_nome: string | null;
  operador_id: number | null;
  horas_totais: string | null;
  horas_faturadas: string | number | null;
  projeto_fechado: boolean | null;
  produto: string | null;
  tipo_chamado: string | null;
  data_real: string | null;
  api_data: unknown;
};

const DEFAULT_BATCH = 50;
/** Atualizações HTTP em paralelo por sublote (só UPDATE; evita INSERT acidental do upsert sem `numero`). */
const UPDATE_CONCURRENCY = 25;

type OsRegraPatch = {
  numero: string;
  empresa_id: number;
  empresa_nome: string;
  valor_hora: number;
  valor_faturamento: number;
  imposto: number;
};

function sanitizeMoney(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function isMissingColumnError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const msg = String((error as { message?: unknown }).message || '');
  return msg.includes('PGRST204');
}

/**
 * Detecta, em runtime, quais colunas extras existem na instância do Supabase.
 * Mantido em módulo para só ser descoberto uma vez por sessão.
 */
const _schemaCache: {
  hasValorHoraRegra?: boolean;
  hasClienteComRegra?: boolean;
} = {};

async function probeExtraColumns(): Promise<{ hasValorHoraRegra: boolean; hasClienteComRegra: boolean }> {
  if (_schemaCache.hasValorHoraRegra !== undefined) {
    return {
      hasValorHoraRegra: _schemaCache.hasValorHoraRegra!,
      hasClienteComRegra: _schemaCache.hasClienteComRegra!,
    };
  }

  // Tenta um update noop em registro inexistente apenas para checar schema
  const r1 = await supabase
    .from('ordens_servico')
    .update({ valor_hora_regra: 0 })
    .eq('numero', '__probe_nonexistent__');
  _schemaCache.hasValorHoraRegra = !isMissingColumnError(r1.error);

  const r2 = await supabase
    .from('ordens_servico')
    .update({ cliente_com_regra_id: 0 })
    .eq('numero', '__probe_nonexistent__');
  _schemaCache.hasClienteComRegra = !isMissingColumnError(r2.error);

  return {
    hasValorHoraRegra: _schemaCache.hasValorHoraRegra,
    hasClienteComRegra: _schemaCache.hasClienteComRegra,
  };
}

async function updateOsFinanceiroComValorHora(
  row: Pick<OsRegraPatch, 'numero' | 'empresa_id' | 'empresa_nome' | 'valor_hora' | 'valor_faturamento' | 'imposto'>,
  schema: Awaited<ReturnType<typeof probeExtraColumns>>
) {
  const payload: Record<string, unknown> = {
    empresa_id: row.empresa_id,
    empresa_nome: row.empresa_nome,
    valor_faturamento: row.valor_faturamento,
    imposto: row.imposto,
  };

  if (schema.hasClienteComRegra) {
    payload.cliente_com_regra_id = row.empresa_id;
    payload.cliente_com_regra_nome = row.empresa_nome;
    payload.regra_aplicada = true;
  }

  if (schema.hasValorHoraRegra) {
    payload.valor_hora_regra = row.valor_hora;
  }

  return supabase.from('ordens_servico').update(payload).eq('numero', row.numero);
}

function readImpostoDecimalFromStorage(): number {
  if (typeof window === 'undefined') return 0.13;
  const s = window.localStorage.getItem('config:imposto');
  const n = parseFloat(s || '');
  return Number.isFinite(n) ? n : 0.13;
}

function produtoNormalizadoParaRegra(ordem: OrdemRow): string | null {
  const raw =
    (ordem.produto && String(ordem.produto).trim()) ||
    String((ordem.api_data as { produto?: { nome?: string } } | null)?.produto?.nome || '').trim();
  if (!raw || raw === 'Produto não informado') return null;
  return raw.toUpperCase().replace(/\s+/g, '_');
}

function ymFromDataReal(dataReal: string | null): string | null {
  if (!dataReal) return null;
  const y = String(dataReal).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(y) ? y : null;
}

function operadorScopeKey(regra: ClienteSubdivisaoRegra): string {
  if (!regra.operador_ids?.length) return 'ALL';
  return `OPS:${[...regra.operador_ids].sort((a, b) => a - b).join(',')}`;
}

/** Bucket compartilhado por regras do mesmo pai/produto(alvo)/tipo/escopo de operador (ex.: FIXO + EXTRA). */
function bucketKeyFromRegra(parent: number, regra: ClienteSubdivisaoRegra): string {
  const prodPart = regra.produto ? regra.produto.toUpperCase() : 'ALL_PRODUCTS';
  return `${parent}|${regra.tipo_projeto}|${prodPart}|${operadorScopeKey(regra)}`;
}

function osMatchesRegraBucket(
  produtoNorm: string | null,
  tipo: 'ABERTO' | 'FECHADO',
  operadorId: number | null,
  regra: ClienteSubdivisaoRegra
): boolean {
  if (regra.tipo_projeto !== tipo) return false;
  if (regra.produto) {
    if (!produtoNorm || produtoNorm !== regra.produto.toUpperCase()) return false;
  }
  if (regra.operador_ids && regra.operador_ids.length > 0) {
    if (operadorId == null || !regra.operador_ids.includes(operadorId)) return false;
  }
  return true;
}

/**
 * Reaplica regras de desmembramento (reclassificação): usa somatório de `horas_totais` no mês-calendário (YYYY-MM)
 * por bucket de regra para decidir FIXO vs EXTRA; em seguida aplica valor/hora da regra (ou histórico do filho),
 * `valor_faturamento` e `imposto` como na tela de correção. Não altera `valor_a_pagar` salvo `recalcularCustosDepois: true`.
 */
export async function executeReprocessamentoRegrasDesmembramentoJob(
  params: ExecuteRegrasDesmembramentoParams = {}
): Promise<{ total: number; processed: number; regrasAplicadas: number; errors: number }> {
  limparCacheRegras();

  // Detecta quais colunas extras existem na instância do Supabase (probe noop)
  const schemaInfo = await probeExtraColumns();

  const batchSize = Math.min(200, Math.max(10, params.batchSize ?? DEFAULT_BATCH));
  const { dataInicio, dataFim } = params;
  const impostoDecimal = params.impostoPercentual ?? readImpostoDecimalFromStorage();

  const pageSize = 1000;
  const all: OrdemRow[] = [];
  for (let from = 0; ; from += pageSize) {
    let q = supabase
      .from('ordens_servico')
      .select(
        `
        id,
        numero,
        empresa_nome,
        empresa_id,
        operador_nome,
        operador_id,
        horas_totais,
        horas_faturadas,
        projeto_fechado,
        produto,
        tipo_chamado,
        data_real,
        api_data
      `
      )
      .order('id')
      .range(from, from + pageSize - 1);

    if (dataInicio) q = q.gte('data_real', dataInicio);
    if (dataFim) q = q.lte('data_real', `${dataFim}T23:59:59.999`);

    const { data, error } = await q;
    if (error) throw error;
    if (!data?.length) break;
    all.push(...(data as OrdemRow[]));
    if (data.length < pageSize) break;
  }

  if (all.length === 0) {
    return { total: 0, processed: 0, regrasAplicadas: 0, errors: 0 };
  }

  const { data: regrasRows, error: regrasErr } = await supabase
    .from('cliente_subdivisao_regras')
    .select('*')
    .eq('ativo', true);
  if (regrasErr) throw regrasErr;

  const regrasAll = (regrasRows || []).map((item: Record<string, unknown>) => ({
    ...item,
    operador_ids: item.operador_ids
      ? Array.isArray(item.operador_ids)
        ? (item.operador_ids as number[])
        : []
      : null,
  })) as ClienteSubdivisaoRegra[];

  const parentsWithRules = new Set<number>();
  const filhoToPai = new Map<number, number>();
  const regrasPorParent = new Map<number, ClienteSubdivisaoRegra[]>();

  for (const r of regrasAll) {
    parentsWithRules.add(r.cliente_pai_id);
    filhoToPai.set(r.cliente_filho_id, r.cliente_pai_id);
    if (!regrasPorParent.has(r.cliente_pai_id)) regrasPorParent.set(r.cliente_pai_id, []);
    regrasPorParent.get(r.cliente_pai_id)!.push(r);
  }
  for (const [, lista] of regrasPorParent) {
    lista.sort((a, b) => a.prioridade - b.prioridade || String(a.id).localeCompare(String(b.id)));
  }

  function resolveClientePai(empresaId: number | null): number | null {
    if (empresaId == null) return null;
    if (parentsWithRules.has(empresaId)) return empresaId;
    return filhoToPai.get(empresaId) ?? null;
  }

  const somaHorasMensalPorBucket = new Map<string, number>();
  for (const ordem of all) {
    const parent = resolveClientePai(ordem.empresa_id);
    if (!parent) continue;
    const produtoNorm = produtoNormalizadoParaRegra(ordem);
    if (!produtoNorm) continue;
    const tipo: 'ABERTO' | 'FECHADO' = ordem.projeto_fechado === true ? 'FECHADO' : 'ABERTO';
    const ym = ymFromDataReal(ordem.data_real);
    if (!ym) continue;
    const h = convertHoursToDecimal(ordem.horas_totais || '00:00:00');
    const opId = ordem.operador_id ?? null;
    const regras = regrasPorParent.get(parent);
    if (!regras?.length) continue;

    const keys = new Set<string>();
    for (const regra of regras) {
      if (!osMatchesRegraBucket(produtoNorm, tipo, opId, regra)) continue;
      keys.add(bucketKeyFromRegra(parent, regra));
    }
    for (const k of keys) {
      const fullKey = `${ym}|${k}`;
      somaHorasMensalPorBucket.set(fullKey, (somaHorasMensalPorBucket.get(fullKey) || 0) + h);
    }
  }

  let regrasAplicadasTotal = 0;
  let errorsTotal = 0;
  let processed = 0;

  for (let i = 0; i < all.length; i += batchSize) {
    if (params.signal?.aborted) break;
    const batch = all.slice(i, i + batchSize);
    const batchUpdates: OsRegraPatch[] = [];

    for (const ordem of batch) {
      try {
        if (!ordem.empresa_id) {
          continue;
        }

        const produtoNorm = produtoNormalizadoParaRegra(ordem);
        if (!produtoNorm) {
          continue;
        }

        const parent = resolveClientePai(ordem.empresa_id);
        if (!parent) {
          continue;
        }

        const horasTotaisDecimal = convertHoursToDecimal(ordem.horas_totais || '00:00:00');
        const tipoProjeto = ordem.projeto_fechado === true ? 'FECHADO' : 'ABERTO';
        const ym = ymFromDataReal(ordem.data_real);

        const desmem = await aplicarRegrasDesmembramento(
          parent,
          ordem.operador_id ?? null,
          produtoNorm,
          tipoProjeto,
          horasTotaisDecimal,
          ordem.numero,
          {
            skipAuditoria: true,
            regrasCache: regrasPorParent,
            condicaoLimiteMenorInclusive: true,
            horasPorRegra: (regra) => {
              if (!ym) return undefined;
              const k = `${ym}|${bucketKeyFromRegra(parent, regra)}`;
              return somaHorasMensalPorBucket.has(k) ? somaHorasMensalPorBucket.get(k)! : 0;
            },
          }
        );

        if (!desmem.clienteRedirecionado || !desmem.regraAplicada) {
          continue;
        }

        const regra = desmem.regraAplicada;
        const dataOS =
          (ordem.data_real && String(ordem.data_real).slice(0, 10)) ||
          String((ordem.api_data as { data?: { real?: string } } | null)?.data?.real || '').slice(0, 10) ||
          new Date().toISOString().split('T')[0];

        const valorHoraRegra = await resolveValorHoraAposRegra(regra, dataOS);

        const { data: cadFilho } = await supabase
          .from('clientes_cadastro')
          .select('nome')
          .eq('empresa_id', regra.cliente_filho_id)
          .maybeSingle();
        const nomeFilho =
          cadFilho?.nome?.trim() || regra.cliente_filho_nome?.trim() || `Cliente ${regra.cliente_filho_id}`;

        const horasFaturadas = convertHoursToDecimal(String(ordem.horas_faturadas ?? '0'));
        const pctWhole = Math.round(impostoDecimal * 100);

        const tipoCh = (ordem.tipo_chamado || '').toLowerCase();
        const semFaturamento = tipoCh === 'atividadeprojeto' || tipoCh === 'orcamento';

        let valorFaturamento = 0;
        let imposto = 0;
        if (!semFaturamento && horasFaturadas > 0 && valorHoraRegra != null && valorHoraRegra > 0) {
          valorFaturamento = Math.round(horasFaturadas * valorHoraRegra * 100) / 100;
          imposto = Math.round(valorFaturamento * (pctWhole / 100) * 100) / 100;
        }

        const valorHoraPersist =
          valorHoraRegra != null && valorHoraRegra > 0
            ? Math.round(valorHoraRegra * 100) / 100
            : horasFaturadas > 0 && valorFaturamento > 0
              ? Math.round((valorFaturamento / horasFaturadas) * 100) / 100
              : 0;

        batchUpdates.push({
          numero: String(ordem.numero || '').trim(),
          empresa_id: regra.cliente_filho_id,
          empresa_nome: nomeFilho,
          valor_hora: sanitizeMoney(valorHoraPersist),
          valor_faturamento: sanitizeMoney(valorFaturamento),
          imposto: sanitizeMoney(imposto),
        });

        regrasAplicadasTotal++;
      } catch (e) {
        console.error(
          `[regras desmembramento] Falha ao preparar OS ${ordem.numero}:`,
          e instanceof Error ? e.message : e
        );
        errorsTotal++;
      }
    }

    processed += batch.length;
    if (batchUpdates.length > 0) {
      for (let j = 0; j < batchUpdates.length; j += UPDATE_CONCURRENCY) {
        const slice = batchUpdates.slice(j, j + UPDATE_CONCURRENCY);
        const results = await Promise.all(
          slice.map((row) => {
            if (!row.numero) {
              return Promise.resolve({
                error: { message: 'numero vazio' } as { message: string },
              });
            }
            return updateOsFinanceiroComValorHora(row, schemaInfo);
          })
        );
        for (let ri = 0; ri < results.length; ri++) {
          const r = results[ri];
          const row = slice[ri];
          if (r.error) {
            console.error(
              `[regras desmembramento] Falha ao atualizar OS ${row.numero}:`,
              r.error.message,
              r.error
            );
            errorsTotal++;
          }
        }
      }
    }

    params.onProgress?.({
      processed: Math.min(processed, all.length),
      total: all.length,
      regrasAplicadas: regrasAplicadasTotal,
      errors: errorsTotal,
    });

    await new Promise((r) => setTimeout(r, 50));
  }

  if (params.signal?.aborted) {
    return { total: all.length, processed, regrasAplicadas: regrasAplicadasTotal, errors: errorsTotal };
  }

  if (params.recalcularCustosDepois === true && all.length > 0) {
    let di = params.dataInicio?.trim() || null;
    let df = params.dataFim?.trim() || null;
    if (!di || !df) {
      const datas = new Set<string>();
      for (const o of all) {
        const y = o.data_real ? String(o.data_real).slice(0, 10) : '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(y)) datas.add(y);
      }
      const arr = [...datas].sort();
      if (arr.length) {
        di = di || arr[0];
        df = df || arr[arr.length - 1];
      }
    }
    if (!di || !df) {
      di = startOfMonthYmdSaoPaulo();
      df = todayYmdSaoPaulo();
    }
    try {
      await executeReprocessamentoCustosJob({
        dataInicio: di,
        dataFim: df,
        numeroOS: '',
        funcionario: undefined,
        impostoPercentual: params.impostoPercentual ?? readImpostoDecimalFromStorage(),
      });
    } catch (e) {
      console.error('[regras desmembramento] Recálculo de custos após regras falhou:', e);
    }
  }

  return { total: all.length, processed, regrasAplicadas: regrasAplicadasTotal, errors: errorsTotal };
}
